import React, { useState } from 'react';
import styled from 'astroturf/react';

import type { ArmSpec, OpenContext } from '../armRegistry';
import { isDownloadRead } from '../openerIntent';
import { getWorkContext } from '../workContext';

const FROST_CAPTION = "There's more inside the PDF";

type Ingredients = {
  thumbnailUrl?: string;
  stats?: string;
  finding?: string;
};

function pluralize(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

function buildStats(
  pageCount?: number,
  figureCount?: number,
  referenceCount?: number,
): string | undefined {
  const parts: string[] = [];
  if (pageCount && pageCount > 0) parts.push(pluralize(pageCount, 'page'));
  if (figureCount && figureCount > 0) {
    parts.push(pluralize(figureCount, 'figure'));
  }
  if (referenceCount && referenceCount > 0) {
    parts.push(pluralize(referenceCount, 'reference'));
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function ingredientsFor(ctx: OpenContext): Ingredients | null {
  if (!isDownloadRead(ctx.detail)) return null;
  const work = getWorkContext();
  const thumbnailUrl = ctx.detail.workThumbnailUrl;
  const stats = work
    ? buildStats(work.pageCount, work.figureCount, work.referenceCount)
    : undefined;
  const finding = work?.takeaways[0];
  if (!thumbnailUrl && !stats && !finding) return null;
  return { thumbnailUrl, stats, finding };
}

const DossierPane: React.FC<Ingredients> = ({
  thumbnailUrl,
  stats,
  finding,
}) => {
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumbnail = Boolean(thumbnailUrl) && !thumbFailed;
  return (
    <Pane>
      <Row>
        {showThumbnail && (
          <Stack>
            <Cover
              src={thumbnailUrl}
              alt=""
              width={48}
              height={64}
              loading="lazy"
              onError={() => setThumbFailed(true)}
            />
          </Stack>
        )}
        <Details>
          {stats && <Stats>{stats}</Stats>}
          {finding && <Finding>{finding}</Finding>}
        </Details>
      </Row>
      <Frost>
        <FrostCaption>{FROST_CAPTION}</FrostCaption>
      </Frost>
    </Pane>
  );
};

export const dossierSpec: ArmSpec = {
  stamp: (ctx) => ({
    render_state: ingredientsFor(ctx) ? 'rendered' : 'fallback',
  }),
  slots: (ctx) => {
    const ingredients = ingredientsFor(ctx);
    if (!ingredients) return undefined;
    return { underHeader: <DossierPane {...ingredients} /> };
  },
};

const Pane = styled.div`
  position: relative;
  overflow: hidden;
  max-height: 12rem;
  margin-bottom: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-12, 12px);
  padding-bottom: var(--spacing-sm-24, 24px);
  text-align: left;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
`;

const Row = styled.div`
  display: flex;
  gap: var(--spacing-xs-16, 16px);
  align-items: flex-start;
`;

const Stack = styled.div`
  flex: 0 0 auto;
  padding-right: 8px;
  padding-bottom: 8px;
`;

const Cover = styled.img`
  display: block;
  width: 48px;
  height: 64px;
  max-width: 100%;
  object-fit: cover;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: var(--border-radius-radius-xs, 2px);
  box-shadow:
    3px 3px 0 -1px var(--background-white, #ffffff),
    3px 3px 0 0 var(--stroke-primary, #dddde2),
    6px 6px 0 -1px var(--background-white, #ffffff),
    6px 6px 0 0 var(--stroke-primary, #dddde2);
`;

const Details = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Stats = styled.div`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-xs-size, 12px);
  line-height: var(--type-body-xs-line-height, 18px);
  font-weight: 600;
  color: var(--text-secondary, #535366);
`;

const Finding = styled.div`
  display: -webkit-box;
  overflow: hidden;
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const Frost = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 2.25rem;
  padding-bottom: var(--spacing-xxs-8, 8px);
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    var(--background-white, #ffffff) 70%
  );
`;

const FrostCaption = styled.span`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-xs-size, 12px);
  font-weight: 600;
  color: var(--text-secondary, #535366);
`;
